/**
 * @license
 * Copyright 2024 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client'

import { BackgroundImageComponent, type BackgroundImageComponentProps } from '@aglyn/shared-ui-jsx/components/background-image.component'
import { mergeSxProps } from '@aglyn/shared-ui-theme'
import {
  onboardingPlanQuery,
  parseOnboardingPlanIntent,
} from '@aglyn/aglyn'
import { continueParam, useContinueUrl } from '@aglyn/shared-util-next'
import { Stack } from '@mui/material'
import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useRef } from 'react'
import {
  isSignUpLandingHeld,
  useSignUpLandingHeld,
} from '../../utils/sign-up-landing-hold'
import { useAuth, useSigninCheck } from '@aglyn/tenant-feature-instance'
import { emailGateWouldRefuse } from '../../utils/email-verification-gate'
import { mintSession } from '../../utils/session-mint'
import { VisitorConsentPill } from '../visitor-consent.component'

export interface AuthenticatingLayoutProps
  extends Partial<BackgroundImageComponentProps> {
  requireEmailVerification?: boolean
  signingOut?: boolean
  /**
   * Suspend every redirect this layout would make (AGL-1524). Set while the
   * page under it is redeeming a one-shot auth action code: the redemption is
   * a client-side call whose in-flight request a navigation can abort, so a
   * layout-level bounce here can silently cancel an email verification while
   * looking exactly like success. The page owns all navigation in that state.
   */
  holdRedirects?: boolean
}

function AuthenticatingLayout(props: AuthenticatingLayoutProps) {
  const {
    children,
    sx,
    requireEmailVerification,
    signingOut,
    holdRedirects,
    ...rest
  } = props
  const router = useRouter()
  const auth = useAuth()
  const { status, data: signInCheckResult } = useSigninCheck()
  const authLoading = status === 'loading'
  const signedIn = signInCheckResult?.signedIn === true
  const emailVerified = signInCheckResult?.user?.emailVerified
  const [, continueUrl, pushContinued] = useContinueUrl()
  const searchParams = useSearchParams()
  // A sign-up still landing on this tab (AGL-3578). Read as state so the
  // effect re-runs on release, and live inside it because the sign-up page
  // raises the hold in an effect of its own, which runs before this one in
  // the same commit, after this render read the state.
  const signUpLanding = useSignUpLandingHeld()
  // The account whose delegated hand-off is under way (AGL-3843). The effect
  // below re-runs on every ID-token emission, and the hand-off's own forced
  // refresh is one, so without this each run minted again: one SSO sign-in in
  // mobile Safari minted four sessions and four device rows in seven seconds.
  const handingOff = useRef<string | null>(null)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    // A one-shot code is being redeemed on this URL — no redirect may fire
    // until the page resolves it (AGL-1524).
    if (holdRedirects) return void 0
    // A sign-up door is still recording the account and creating its
    // workspace, and ends in its own navigation into it (AGL-3578). Pushing
    // to `/` now is what tore a phone sign-up down before it got there.
    if (signUpLanding || isSignUpLandingHeld()) return void 0
    if (authLoading) return void 0
    if (signedIn && signingOut) return void 0
    if (!signedIn && !signingOut) return void 0
    if (signingOut)
      // Forward the continue param so an idle-expired session resumes on
      // the page it left off after re-authenticating (AGL-464).
      return void router.push(
        continueUrl
          ? `/signin?${continueParam(encodeURIComponent(continueUrl))}`
          : '/signin',
      )
    // Only /verify-email sets `requireEmailVerification`, so an unverified
    // session is already where it belongs: stay put. Pushing the route here
    // re-ran on every signed-in-check emission — the page's poll reloads the
    // user every few seconds — which refetched the page each time and dropped
    // its `?continue=` destination on the first tick (AGL-3384).
    if (requireEmailVerification && !emailVerified) return void 0

    // Delegated cross-origin return (AGL-465/466): the workspace subdomain
    // signs in silently from the shared __session cookie, so that cookie
    // MUST exist before we hand back — otherwise its check 401s and it
    // bounces the user to the auth host again: a redirect loop. Mint it and
    // AWAIT before the hard navigation, which would otherwise abort the
    // in-flight mint. A same-origin '/' return keeps the client-nav path
    // (no race — the mint stays in flight).
    if (/^https?:\/\//.test(continueUrl)) {
      // Use the live SDK user (auth.currentUser) — the most reliable source
      // of a fresh ID token; signInCheckResult.user can lag.
      const user = auth.currentUser ?? signInCheckResult?.user ?? null
      if (!user) {
        // Signed-in but no user object yet — wait for the next tick rather
        // than hand off with no cookie.
        return void 0
      }
      // Once per sign-in (AGL-3843). A re-run for the same account neither
      // mints again nor cancels the run in flight: that run owns the
      // navigation, and only leaving the page stops it.
      if (handingOff.current === user.uid) return void 0
      handingOff.current = user.uid
      void (async () => {
        // An unverified password account cannot have the cookie (AGL-479),
        // so handing it off lands it session-less on the other origin, which
        // sends it straight back here to be handed off again — a phone
        // password sign-up looped like that and never saw /verify-email, so
        // no verification mail was ever sent (AGL-3690). Verify HERE, on the
        // origin that holds the session; /verify-email mints the cookie and
        // carries the continue URL on once the address is confirmed.
        if (await emailGateWouldRefuse(user)) {
          handingOff.current = null
          if (mounted.current) {
            router.replace(
              `/verify-email?${continueParam(encodeURIComponent(continueUrl))}`,
            )
          }
          return
        }
        // Force a token refresh, and confirm the mint returned before handing
        // off so the workspace's silent sign-in has a cookie to read
        // (AGL-467). Shared with any mint already in flight for this account
        // (`useSessionCookie` mints on the same landing), so the browser gets
        // one session and one device row. A refusal is logged by the mint.
        const minted = await mintSession(user, { current: null }, { forceRefresh: true })
        if (!minted) console.error('[auth] delegated mint failed before hand-off')
        if (
          mounted.current &&
          auth.currentUser?.uid === user.uid &&
          typeof window !== 'undefined'
        ) {
          window.location.assign(continueUrl)
        } else {
          handingOff.current = null
        }
      })()
      return void 0
    }

    // Carry a marketing plan CTA through the auth bounce (AGL-1117). Someone
    // already signed in — or signing in to upgrade — hits /signup?plan=pro and
    // would otherwise land on their sites with the plan silently dropped. The
    // home jump reads it back off the URL and routes to billing instead.
    //
    // Serialized through the shared writer (AGL-1535): appending `&interval=`
    // by hand here turned "the CTA stated no interval" into "the CTA said
    // monthly" one redirect after the fact, and the billing page's toggle —
    // which deliberately only obeys a STATED interval — could no longer tell.
    const intent = parseOnboardingPlanIntent(searchParams)
    return void pushContinued(intent ? `/?${onboardingPlanQuery(intent)}` : '/')
  }, [
    auth,
    authLoading,
    continueUrl,
    emailVerified,
    holdRedirects,
    signUpLanding,
    signingOut,
    pushContinued,
    requireEmailVerification,
    router,
    searchParams,
    signedIn,
    signInCheckResult,
  ])

  return (
    <BackgroundImageComponent
      url="/_static/images/backgrounds/patterns/abstract-wave-lines.svg"
      sx={mergeSxProps(
        {
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, md: 3 },
          color: 'text.primary',
        },
        sx,
      )}
      {...rest}
    >
      <Stack
        direction="column"
        spacing={2}
        sx={{
          justifyContent: "center",
          alignItems: "center",
          maxWidth: 1,
          width: 440
        }}>
        {children}
      </Stack>
      {/* The privacy control for a page with no account menu.

          Mounted on the SHELL rather than on each page, which is what makes
          the placement structural: every unauthenticated route — sign-in,
          sign-up, account recovery, SSO, email verification, sign-out —
          renders through here, so a new one inherits the control instead of
          needing someone to remember it. The signed-in console puts the same
          control in the account menu and floats nothing.

          `/signin` is this surface's most-collected page and it is reachable
          on more than one host: interactive sign-in is delegated to
          `auth.<workspace domain>` for mobile and for workspace subdomains,
          so the control has to exist wherever this shell renders rather than
          wherever the console apex does. */}
      <VisitorConsentPill />
    </BackgroundImageComponent>
  );
}
AuthenticatingLayout.displayName = 'AuthenticatingLayout'
AuthenticatingLayout.aglyn = true

export { AuthenticatingLayout }
export default AuthenticatingLayout
